/* eslint-disable import/prefer-default-export */

const num = (v) => {
    if (v === '' || v === null || v === undefined) return NaN;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    return Number.isFinite(n) ? n : NaN;
};

const finiteOrUndefined = (n) => (Number.isFinite(n) ? n : undefined);

const compactOptionalNumbers = (fields) => {
    const out = {};
    Object.entries(fields).forEach(([key, value]) => {
        if (value !== undefined) {
            out[key] = value;
        }
    });
    return out;
};

/** Fuel Ledger HTML defaults for settings that are configurable per fuel policy. */
export const FUEL_LEDGER_PROFILE_DEFAULTS = Object.freeze({
    microRefillL: null,
    gapCapMin: 15,
    drainSpanMin: 20,
    maxBurnRateLh: 40,
});

/**
 * Placeholders sent only on incomplete DRAFT/ARCHIVED API saves when the user left
 * tracking tolerances empty. The form stays blank; values are filled before Active.
 */
const DRAFT_TRACKING_TOLERANCE_DEFAULTS = {
    tankCapacity: 100,
    refillThreshold: 3,
    drainTheft: 3,
    spikeFilter: 3,
    sloshDuration: 30,
    sloshTolerance: 3,
};

const resolveDraftNumericField = (formValue, fallback) => {
    const n = num(formValue);
    return Number.isFinite(n) ? n : fallback;
};

const isIncompleteDraftPolicyStatus = (status) => {
    const normalized = String(status || '').toUpperCase();
    return normalized === 'DRAFT' || normalized === 'ARCHIVED';
};

const resolveDraftPlaceholderMarkers = (policy) => (
    policy?.draftPlaceholderFields || policy?.draft_placeholder_fields || null
);

const numericFormValue = (value) => (
    value != null && value !== '' ? String(value) : ''
);

const positivePolicyValueOr = (value, fallback) => {
    const n = num(value);
    return Number.isFinite(n) && n > 0 ? n : fallback;
};

const stripMarkedDraftPlaceholderFields = (form, markers) => {
    const next = { ...form };

    if (markers.telemetry) {
        next.telemetryInput = '';
    }
    if (markers.levelSource) {
        next.tankCapacityDetectionEnabled = false;
        next.tableMapEnabled = false;
        next.equationMapEnabled = false;
    }
    if (markers.tankCapacity) {
        next.tankCapacityL = '';
    }
    if (markers.refillThreshold) {
        next.refillDetectionThresholdL = '';
    }
    if (markers.drainTheft) {
        next.theftDrainThresholdL = '';
    }
    if (markers.spikeFilter) {
        next.spikeFilterVolumeL = '';
    }
    if (markers.sloshDuration) {
        next.sloshSettleDurationSec = '';
    }
    if (markers.sloshTolerance) {
        next.sloshToleranceL = '';
    }

    return next;
};

/** Fallback for drafts saved before draftPlaceholderFields was persisted. */
const stripInferredDraftPlaceholderFields = (form, policy) => {
    const next = { ...form };
    const defaults = DRAFT_TRACKING_TOLERANCE_DEFAULTS;
    const rawTelemetry = policy?.telemetryInput;
    const rawLevelSource = normalizeLevelSource(policy?.levelSource);
    const tableMap = policy?.tableMap || {};
    const equationMap = policy?.equationMap || {};

    const stripIfDefault = (field, placeholder) => {
        const n = num(next[field]);
        if (Number.isFinite(n) && n === placeholder) {
            next[field] = '';
        }
    };

    stripIfDefault('tankCapacityL', defaults.tankCapacity);
    stripIfDefault('refillDetectionThresholdL', defaults.refillThreshold);
    stripIfDefault('theftDrainThresholdL', defaults.drainTheft);
    stripIfDefault('spikeFilterVolumeL', defaults.spikeFilter);
    stripIfDefault('sloshSettleDurationSec', defaults.sloshDuration);
    stripIfDefault('sloshToleranceL', defaults.sloshTolerance);

    if (rawTelemetry === TELEMETRY_INPUT_VALUES.BOTH) {
        next.telemetryInput = '';
    }

    const numericsMatchDefaults = (
        num(next.tankCapacityL || defaults.tankCapacity) === defaults.tankCapacity
        && num(next.refillDetectionThresholdL || defaults.refillThreshold) === defaults.refillThreshold
        && num(next.theftDrainThresholdL || defaults.drainTheft) === defaults.drainTheft
        && num(next.spikeFilterVolumeL || defaults.spikeFilter) === defaults.spikeFilter
        && num(next.sloshSettleDurationSec || defaults.sloshDuration) === defaults.sloshDuration
        && num(next.sloshToleranceL || defaults.sloshTolerance) === defaults.sloshTolerance
    );

    if (
        rawLevelSource === LEVEL_SOURCE_VALUES.TANK_CAPACITY_DETECTION
        && !tableMap.enabled
        && !equationMap.enabled
        && rawTelemetry === TELEMETRY_INPUT_VALUES.BOTH
        && numericsMatchDefaults
    ) {
        next.tankCapacityDetectionEnabled = false;
        next.tableMapEnabled = false;
        next.equationMapEnabled = false;
    }

    return next;
};

const stripDraftPlaceholderFields = (form, policy) => {
    const status = form.status || policy?.policyStatus || policy?.status;
    if (!isIncompleteDraftPolicyStatus(status)) {
        return form;
    }

    const markers = resolveDraftPlaceholderMarkers(policy);
    if (markers && typeof markers === 'object') {
        return stripMarkedDraftPlaceholderFields(form, markers);
    }

    return stripInferredDraftPlaceholderFields(form, policy);
};

const buildDraftPlaceholderMarkers = (form) => {
    const markers = {};

    if (!telemetryInputFromForm(form)) {
        markers.telemetry = true;
    }
    if (!levelSourceFromForm(form)) {
        markers.levelSource = true;
    }
    if (!Number.isFinite(num(form.tankCapacityL))) {
        markers.tankCapacity = true;
    }
    if (!Number.isFinite(num(form.refillDetectionThresholdL))) {
        markers.refillThreshold = true;
    }
    if (!Number.isFinite(num(form.theftDrainThresholdL))) {
        markers.drainTheft = true;
    }
    if (!Number.isFinite(num(form.spikeFilterVolumeL))) {
        markers.spikeFilter = true;
    }
    if (!Number.isFinite(num(form.sloshSettleDurationSec))) {
        markers.sloshDuration = true;
    }
    if (!Number.isFinite(num(form.sloshToleranceL))) {
        markers.sloshTolerance = true;
    }

    return Object.keys(markers).length > 0 ? markers : null;
};

export const TELEMETRY_INPUT_VALUES = {
    LITRE: 'LITRE',
    PERCENTAGE: 'PERCENTAGE',
    BOTH: 'BOTH',
    VOLTAGE: 'VOLTAGE',
};

export function isVoltageTelemetryInput(telemetryInput) {
    return telemetryInput === 'voltage' || telemetryInput === TELEMETRY_INPUT_VALUES.VOLTAGE;
}

export function telemetryInputFromForm(form) {
    const v = form.telemetryInput;
    if (v === 'litre' || v === TELEMETRY_INPUT_VALUES.LITRE) return TELEMETRY_INPUT_VALUES.LITRE;
    if (v === 'percentage' || v === TELEMETRY_INPUT_VALUES.PERCENTAGE) return TELEMETRY_INPUT_VALUES.PERCENTAGE;
    if (v === 'both' || v === 'litrePercentage' || v === TELEMETRY_INPUT_VALUES.BOTH) return TELEMETRY_INPUT_VALUES.BOTH;
    if (v === 'voltage' || v === TELEMETRY_INPUT_VALUES.VOLTAGE) return TELEMETRY_INPUT_VALUES.VOLTAGE;
    return undefined;
}

export function telemetryInputToFormValue(telemetryInput) {
    if (telemetryInput === TELEMETRY_INPUT_VALUES.LITRE) return 'litre';
    if (telemetryInput === TELEMETRY_INPUT_VALUES.PERCENTAGE) return 'percentage';
    if (telemetryInput === TELEMETRY_INPUT_VALUES.BOTH) return 'litrePercentage';
    if (telemetryInput === TELEMETRY_INPUT_VALUES.VOLTAGE) return 'voltage';
    return '';
}

/** Unit label for fuel map table level columns from form telemetryInput value. */
export function fuelMapLevelUnitForFormTelemetry(telemetryInput) {
    if (telemetryInput === 'litre') return 'L';
    if (telemetryInput === 'percentage') return '%';
    if (telemetryInput === 'litrePercentage') return 'L / %';
    if (isVoltageTelemetryInput(telemetryInput)) return 'V';
    return 'L';
}

/** Unit label for fuel map table mapped column from form telemetryInput value. */
export function fuelMapMappedUnitForFormTelemetry(telemetryInput) {
    if (isVoltageTelemetryInput(telemetryInput)) return 'L';
    if (telemetryInput === 'percentage') return 'L';
    return '%';
}

export const LEVEL_SOURCE_VALUES = {
    MAP_TABLE: 'MAP TABLE',
    MAP_EQUATION: 'MAP EQUATION',
    TANK_CAPACITY_DETECTION: 'FUEL TANK CAPACITY DETECTION',
};

const LEVEL_SOURCE_CANONICAL = {
    MAP_TABLE: LEVEL_SOURCE_VALUES.MAP_TABLE,
    MAP_EQUATION: LEVEL_SOURCE_VALUES.MAP_EQUATION,
    FUEL_TANK_CAPACITY_DETECTION: LEVEL_SOURCE_VALUES.TANK_CAPACITY_DETECTION,
};

/** Accept space or underscore input; always return API space form when recognized. */
export function normalizeLevelSource(value) {
    if (!value || typeof value !== 'string') {
        return value ?? null;
    }
    const normalized = value.trim().toUpperCase().replace(/\s+/g, '_');
    return LEVEL_SOURCE_CANONICAL[normalized] ?? value.trim();
}

/** Human-readable label for API levelSource values. */
export function levelSourceToDisplayLabel(levelSource) {
    const canonical = normalizeLevelSource(levelSource);
    if (canonical === LEVEL_SOURCE_VALUES.MAP_TABLE) return 'Map Table';
    if (canonical === LEVEL_SOURCE_VALUES.MAP_EQUATION) return 'Map Equation';
    if (canonical === LEVEL_SOURCE_VALUES.TANK_CAPACITY_DETECTION) return 'Fuel Tank Capacity Detection';
    return levelSource || '—';
}

export function levelSourceFromForm(form) {
    if (telemetryInputFromForm(form) === TELEMETRY_INPUT_VALUES.VOLTAGE) {
        return LEVEL_SOURCE_VALUES.MAP_EQUATION;
    }
    if (form.tankCapacityDetectionEnabled) return LEVEL_SOURCE_VALUES.TANK_CAPACITY_DETECTION;
    if (form.tableMapEnabled) return LEVEL_SOURCE_VALUES.MAP_TABLE;
    if (form.equationMapEnabled) return LEVEL_SOURCE_VALUES.MAP_EQUATION;
    return undefined;
}

export function levelSourceToFormFlags(levelSource, tableMap, equationMap) {
    const tm = tableMap || {};
    const em = equationMap || {};
    const canonical = normalizeLevelSource(levelSource);
    if (canonical === LEVEL_SOURCE_VALUES.MAP_TABLE) {
        return { tableMapEnabled: true, equationMapEnabled: false, tankCapacityDetectionEnabled: false };
    }
    if (canonical === LEVEL_SOURCE_VALUES.MAP_EQUATION) {
        return { tableMapEnabled: false, equationMapEnabled: true, tankCapacityDetectionEnabled: false };
    }
    if (canonical === LEVEL_SOURCE_VALUES.TANK_CAPACITY_DETECTION) {
        return { tableMapEnabled: false, equationMapEnabled: false, tankCapacityDetectionEnabled: true };
    }
    return {
        tableMapEnabled: !!tm.enabled,
        equationMapEnabled: !!em.enabled,
        tankCapacityDetectionEnabled: false,
    };
}

/** Normalize a policy document from fetch/edit into UI-friendly shape. */
export function normalizeFuelPolicyFromApi(doc) {
    if (!doc) return null;
    const tm = doc.tableMap || {};
    const em = doc.equationMap || {};
    const gnss = doc.gnssVirtualFuelCoefficients || doc.gnssVirtualFuel || {};
    const levelFlags = levelSourceToFormFlags(doc.levelSource, tm, em);
    const rawMicroRefillL = doc.microRefillL ?? doc.micro_refill_l;
    const rawGapCapMin = doc.gapCapMin ?? doc.offlineGapCapMin ?? doc.offline_gap_cap_min;
    const rawDrainSpanMin = doc.drainSpanMin ?? doc.drain_window_min;
    const rawMaxBurnRateLh = doc.maxBurnRateLh ?? doc.max_burn_rate_lh;

    return {
        oid: doc.oid || doc.id,
        id: doc.oid || doc.id,
        name: doc.name || '',
        status: doc.policyStatus || doc.status || 'DRAFT',
        policyStatus: doc.policyStatus || doc.status || 'DRAFT',
        tankCapacityL: doc.tankCapacity ?? doc.tankCapacityL,
        refillDetectionThresholdL: doc.refillThreshold ?? doc.refillDetectionThresholdL,
        theftDrainThresholdL: doc.drainTheft ?? doc.theftDrainThresholdL,
        spikeFilterVolumeL: doc.spikeFilter ?? doc.spikeFilterVolumeL,
        sloshSettleDurationSec: doc.sloshDuration ?? doc.sloshSettleDurationSec,
        sloshToleranceL: doc.sloshTolerance ?? doc.sloshToleranceL,
        // Protobuf encodes an absent scalar as 0; treat it as the HTML default/off state.
        microRefillL: positivePolicyValueOr(
            rawMicroRefillL,
            FUEL_LEDGER_PROFILE_DEFAULTS.microRefillL,
        ),
        gapCapMin: positivePolicyValueOr(rawGapCapMin, FUEL_LEDGER_PROFILE_DEFAULTS.gapCapMin),
        drainSpanMin: positivePolicyValueOr(
            rawDrainSpanMin,
            FUEL_LEDGER_PROFILE_DEFAULTS.drainSpanMin,
        ),
        maxBurnRateLh: positivePolicyValueOr(
            rawMaxBurnRateLh,
            FUEL_LEDGER_PROFILE_DEFAULTS.maxBurnRateLh,
        ),
        levelSource: normalizeLevelSource(doc.levelSource),
        telemetryInput: doc.telemetryInput,
        voltageRange: doc.voltageRange || {},
        draftPlaceholderFields: doc.draftPlaceholderFields || doc.draft_placeholder_fields,
        tableMap: tm,
        equationMap: em,
        tableMapEnabled: levelFlags.tableMapEnabled,
        equationMapEnabled: levelFlags.equationMapEnabled,
        tankCapacityDetectionEnabled: levelFlags.tankCapacityDetectionEnabled,
        gnssVirtualFuel: gnss,
        gnssVirtualFuelCoefficients: gnss,
        percentageMapping: doc.percentageMapping || [],
        description: doc.description || '',
        associatedAsset: doc.associatedAsset || doc.associatedAssets || [],
        assetsAttached: typeof doc.assetsAttached === 'number'
            ? doc.assetsAttached
            : (doc.associatedAsset || []).length,
        createdAt: doc.createdAt,
        lastEditedAt: resolveFuelPolicyLastEditedAt(doc),
    };
}

/** Prefer latest update timestamp; fall back to createdAt only when no update field exists. */
export function resolveFuelPolicyLastEditedAt(doc) {
    if (!doc) {
        return null;
    }
    const updated = doc.lastEditedAt
        ?? doc.updatedAt
        ?? doc.modifiedAt
        ?? doc.lastUpdated
        ?? doc.dateModified
        ?? doc.lastModified;
    if (updated) {
        return updated;
    }
    return doc.createdAt ?? null;
}

/** Extract saved fuel policy document from create/update API response. */
export function parseFuelPolicyFromSaveResponse(result) {
    const payload = result?.response?.data ?? result?.data ?? result;
    if (!payload) {
        return null;
    }
    let raw = payload.data ?? payload.fuelPolicy ?? payload;
    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch {
            return null;
        }
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return normalizeFuelPolicyFromApi(raw);
    }
    return null;
}

/** Map API policy → add/edit form state. */
export function apiPolicyToForm(policy) {
    const p = normalizeFuelPolicyFromApi(policy);
    if (!p) return null;
    const tm = p.tableMap || {};
    const em = p.equationMap || {};
    const gnss = p.gnssVirtualFuel || {};

    const form = {
        name: p.name || '',
        status: p.status || 'DRAFT',
        tankCapacityL: numericFormValue(
            p.tankCapacityL != null && p.tankCapacityL !== ''
                ? p.tankCapacityL
                : policy.tankCapacity,
        ),
        refillDetectionThresholdL: numericFormValue(p.refillDetectionThresholdL),
        theftDrainThresholdL: numericFormValue(p.theftDrainThresholdL),
        spikeFilterVolumeL: numericFormValue(p.spikeFilterVolumeL),
        sloshSettleDurationSec: numericFormValue(p.sloshSettleDurationSec),
        sloshToleranceL: numericFormValue(p.sloshToleranceL),
        microRefillL: numericFormValue(p.microRefillL),
        gapCapMin: numericFormValue(p.gapCapMin),
        drainSpanMin: numericFormValue(p.drainSpanMin),
        maxBurnRateLh: numericFormValue(p.maxBurnRateLh),
        telemetryInput: telemetryInputToFormValue(p.telemetryInput),
        voltageMin: numericFormValue(p.voltageRange?.vMin ?? p.voltageRange?.v_min),
        voltageMax: numericFormValue(p.voltageRange?.vMax ?? p.voltageRange?.v_max),
        tankCapacityDetectionEnabled: p.tankCapacityDetectionEnabled,
        tableMapEnabled: p.tableMapEnabled,
        equationMapEnabled: p.equationMapEnabled,
        fuelMapRows: Array.isArray(tm.mapping) ? tm.mapping : [],
        equationMap: em.mapping && typeof em.mapping === 'object'
            ? {
                a2: em.mapping.a2 ?? em.mapping.A2 ?? 0,
                a1: em.mapping.a1 ?? em.mapping.A1 ?? 0,
                a0: em.mapping.a0 ?? em.mapping.A0 ?? 0,
            }
            : { a2: 0, a1: 0, a0: 0 },
        idleLhr: gnss.idleLhr != null ? String(gnss.idleLhr) : '',
        cityL100km: gnss.cityL100km != null ? String(gnss.cityL100km) : '',
        avgL100km: gnss.avgL100km != null ? String(gnss.avgL100km) : '',
        hwyL100km: gnss.hwyL100km != null ? String(gnss.hwyL100km) : '',
        penaltyPct: gnss.penaltyPct != null ? String(gnss.penaltyPct) : '',
        penaltyIntervalKmh: gnss.penaltyIntervalKmh != null ? String(gnss.penaltyIntervalKmh) : '',
        percentageMapping: Array.isArray(p.percentageMapping) ? p.percentageMapping : [],
        description: p.description || '',
    };

    if (form.telemetryInput === 'voltage') {
        form.tableMapEnabled = false;
        form.equationMapEnabled = true;
        form.tankCapacityDetectionEnabled = false;
    }

    if (form.telemetryInput === 'litrePercentage') {
        form.tableMapEnabled = false;
        form.equationMapEnabled = false;
        form.tankCapacityDetectionEnabled = true;
    }

    return stripDraftPlaceholderFields(form, policy);
}

/** Map form → create/update request body (`fuelPolicy` wrapper). */
export function buildFuelPolicyPayload(form, oid) {
    const policyStatus = form.status || 'DRAFT';
    const isActiveSave = policyStatus === 'ACTIVE';
    const tank = num(form.tankCapacityL);

    let levelSource = levelSourceFromForm(form);
    let telemetryInput = telemetryInputFromForm(form);
    const isVoltagePolicy = telemetryInput === TELEMETRY_INPUT_VALUES.VOLTAGE;

    // Backend schema still expects core calibration keys even for DRAFT saves.
    if (!isActiveSave) {
        if (!telemetryInput) {
            telemetryInput = TELEMETRY_INPUT_VALUES.BOTH;
        }
        if (!levelSource) {
            levelSource = isVoltagePolicy
                ? LEVEL_SOURCE_VALUES.MAP_EQUATION
                : LEVEL_SOURCE_VALUES.TANK_CAPACITY_DETECTION;
        }
    }

    const equationMapEnabled = isVoltagePolicy || !!form.equationMapEnabled;
    const tableMapEnabled = isVoltagePolicy ? false : !!form.tableMapEnabled;

    const gnssVirtualFuelCoefficients = compactOptionalNumbers({
        idleLhr: finiteOrUndefined(num(form.idleLhr)),
        cityL100km: finiteOrUndefined(num(form.cityL100km)),
        avgL100km: finiteOrUndefined(num(form.avgL100km)),
        hwyL100km: finiteOrUndefined(num(form.hwyL100km)),
        penaltyPct: finiteOrUndefined(num(form.penaltyPct)),
        penaltyIntervalKmh: finiteOrUndefined(num(form.penaltyIntervalKmh)),
    });

    const draftDefaults = DRAFT_TRACKING_TOLERANCE_DEFAULTS;
    const tankCapacity = isActiveSave
        ? finiteOrUndefined(tank)
        : resolveDraftNumericField(form.tankCapacityL, draftDefaults.tankCapacity);
    const refillThreshold = isActiveSave
        ? finiteOrUndefined(num(form.refillDetectionThresholdL))
        : resolveDraftNumericField(form.refillDetectionThresholdL, draftDefaults.refillThreshold);
    const drainTheft = isActiveSave
        ? finiteOrUndefined(num(form.theftDrainThresholdL))
        : resolveDraftNumericField(form.theftDrainThresholdL, draftDefaults.drainTheft);
    const spikeFilter = isActiveSave
        ? finiteOrUndefined(num(form.spikeFilterVolumeL))
        : resolveDraftNumericField(form.spikeFilterVolumeL, draftDefaults.spikeFilter);
    const sloshDuration = isActiveSave
        ? finiteOrUndefined(num(form.sloshSettleDurationSec))
        : resolveDraftNumericField(form.sloshSettleDurationSec, draftDefaults.sloshDuration);
    const sloshTolerance = isActiveSave
        ? finiteOrUndefined(num(form.sloshToleranceL))
        : resolveDraftNumericField(form.sloshToleranceL, draftDefaults.sloshTolerance);

    const fuelPolicy = {
        name: form.name?.trim(),
        policyStatus,
        tankCapacity,
        refillThreshold,
        drainTheft,
        spikeFilter,
        sloshDuration,
        sloshTolerance,
        // Zero is the wire-level sentinel used by the API to clear/disable Micro-refill.
        microRefillL: finiteOrUndefined(num(form.microRefillL)) ?? 0,
        gapCapMin: finiteOrUndefined(num(form.gapCapMin))
            ?? FUEL_LEDGER_PROFILE_DEFAULTS.gapCapMin,
        drainSpanMin: finiteOrUndefined(num(form.drainSpanMin))
            ?? FUEL_LEDGER_PROFILE_DEFAULTS.drainSpanMin,
        maxBurnRateLh: finiteOrUndefined(num(form.maxBurnRateLh))
            ?? FUEL_LEDGER_PROFILE_DEFAULTS.maxBurnRateLh,
        telemetryInput,
        levelSource,
        tableMap: {
            enabled: tableMapEnabled,
            mapping: tableMapEnabled
                ? (form.fuelMapRows || []).map((row) => ({
                    fuelLevelFrom: num(row.fuelLevelFrom),
                    fuelLevelTo: num(row.fuelLevelTo),
                    mappedValue: num(row.mappedValue),
                }))
                : [],
        },
        equationMap: {
            enabled: equationMapEnabled,
            mapping: equationMapEnabled
                ? {
                    a2: finiteOrUndefined(num(form.equationMap?.a2)) ?? 0,
                    a1: finiteOrUndefined(num(form.equationMap?.a1)) ?? 0,
                    a0: finiteOrUndefined(num(form.equationMap?.a0)) ?? 0,
                }
                : { a2: 0, a1: 0, a0: 0 },
        },
        percentageMapping: (form.percentageMapping || []).map((row) => ({
            percentageFrom: num(row.percentageFrom),
            percentageTo: num(row.percentageTo),
            state: row.state || '',
        })),
        description: form.description || '',
    };

    if (telemetryInput === TELEMETRY_INPUT_VALUES.VOLTAGE) {
        const vMin = finiteOrUndefined(num(form.voltageMin));
        const vMax = finiteOrUndefined(num(form.voltageMax));
        if (vMin !== undefined || vMax !== undefined) {
            fuelPolicy.voltageRange = { vMin, vMax };
        }
    }

    if (isActiveSave) {
        fuelPolicy.gnssVirtualFuelCoefficients = {
            idleLhr: finiteOrUndefined(num(form.idleLhr)),
            cityL100km: finiteOrUndefined(num(form.cityL100km)),
            avgL100km: finiteOrUndefined(num(form.avgL100km)),
            hwyL100km: finiteOrUndefined(num(form.hwyL100km)),
            penaltyPct: finiteOrUndefined(num(form.penaltyPct)),
            penaltyIntervalKmh: finiteOrUndefined(num(form.penaltyIntervalKmh)),
        };
    } else if (Object.keys(gnssVirtualFuelCoefficients).length > 0) {
        fuelPolicy.gnssVirtualFuelCoefficients = gnssVirtualFuelCoefficients;
    }

    if (!isActiveSave) {
        const draftPlaceholderFields = buildDraftPlaceholderMarkers(form);
        if (draftPlaceholderFields) {
            fuelPolicy.draftPlaceholderFields = draftPlaceholderFields;
        }
    }

    if (oid) {
        fuelPolicy.oid = oid;
    } else {
        fuelPolicy.associatedAsset = [];
    }

    return { fuelPolicy };
}

export function isFuelPolicySaveSuccess(result) {
    const httpStatus = result?.response?.status ?? result?.status;
    const bodyStatus = result?.response?.data?.status ?? result?.data?.status;
    return httpStatus === 200 || bodyStatus === 200;
}

export function extractFuelPolicyApiErrorMessage(result) {
    const data = result?.response?.data ?? result?.data;
    if (typeof data === 'string' && data.trim()) {
        return data;
    }
    if (data?.error?.message) {
        return data.error.message;
    }
    if (typeof data?.error === 'string') {
        return data.error;
    }
    if (data?.message) {
        return data.message;
    }
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
        return data.errors
            .map((entry) => (typeof entry === 'string' ? entry : entry?.message))
            .filter(Boolean)
            .join('; ');
    }
    if (result?.message) {
        return result.message;
    }
    return 'Unable to save fuel policy. Please check your entries and try again.';
}

/** Parse fetchFuelPolicies response (`data` may be JSON string). */
export function parseFuelPoliciesListResponse(result) {
    const payload = result?.data ?? result;
    const status = payload?.status ?? result?.status;
    let raw = payload?.data;

    if (typeof raw === 'string') {
        try {
            raw = JSON.parse(raw);
        } catch {
            raw = [];
        }
    }

    if (Array.isArray(raw)) {
        return {
            status,
            totalData: payload?.totalData ?? raw.length,
            policies: raw.map((doc) => normalizeFuelPolicyFromApi(doc)),
        };
    }

    if (Array.isArray(payload?.fuelPolicies)) {
        return {
            status,
            totalData: payload?.totalData ?? payload.fuelPolicies.length,
            policies: payload.fuelPolicies.map((doc) => normalizeFuelPolicyFromApi(doc)),
        };
    }

    if (Array.isArray(payload)) {
        return {
            status,
            totalData: payload.length,
            policies: payload.map((doc) => normalizeFuelPolicyFromApi(doc)),
        };
    }

    return { status, totalData: 0, policies: [] };
}

export function isActiveFuelPolicy(policy) {
    const status = policy?.policyStatus || policy?.status;
    return String(status || '').toUpperCase() === 'ACTIVE';
}

/** Keep only policies with policyStatus ACTIVE (e.g. vehicle preset hardware bindings). */
export function filterActiveFuelPolicies(policies = []) {
    return (Array.isArray(policies) ? policies : []).filter(isActiveFuelPolicy);
}

/** Query params for vehicle preset fuel policy dropdown — ACTIVE policies only. */
export function buildHardwareBindingFuelPoliciesParams() {
    return buildFetchFuelPoliciesParams({ page: 1, perPage: 500, status: 'ACTIVE' });
}

/** Query params for GET /fuelPolicies (pagination + optional filters). */
export function buildFetchFuelPoliciesParams({ page, perPage, searchKey, status }) {
    const params = {
        pageNumber: page,
        totalPerPage: perPage,
    };

    const filter = {};
    if (searchKey) {
        filter.name = { type: 'contains', value: searchKey };
    }
    if (status) {
        filter.policyStatus = { type: 'equals', value: status };
    }

    if (Object.keys(filter).length > 0) {
        params.filter = filter;
    }

    params.pagination = {
        pageNumber: page,
        totalPerPage: perPage,
    };

    return params;
}
